import { t } from "i18next";
import i18n from '../i18n/config'
import moment from "moment";

/**
 * Get if `groupLink` is valid.
 * @param groupLink
 * @returns {boolean}
 */
export const isGroupLinkValid = (groupLink: any = {}) => {
  return groupLink.value && !groupLink.disabled;
};

/**
 * Get if `groupLink` is active (valid and no new activation to do).
 * @param groupLink
 * @returns {boolean}
 */
export const isGroupLinkActive = (groupLink: any = {}) => {
  return isGroupLinkValid(groupLink) && !groupLink.newActivation;
};

/**
 * Get if `groupLink` is a new activation.
 * @param groupLink
 * @returns {boolean}
 */
export const isGroupLinkNewActivation = (groupLink: any = {}) => {
  return isGroupLinkValid(groupLink) && groupLink.newActivation;
};

/**
 * Get if `groupLink` is a valid parent.
 * @param groupLink
 * @returns {boolean}
 */
export const isGroupLinkParent = (groupLink: any = {}) => {
  return isGroupLinkActive(groupLink) && groupLink.isParent;
};

/**
 * Get if `groupLink` is a valid child.
 * @param groupLink
 * @returns {boolean}
 */
export const isGroupLinkChild = (groupLink: any = {}) => {
  return isGroupLinkActive(groupLink) && groupLink.isChild;
};

/**
 * Table sort function
 * @param order
 * @param orderAs
 * @param orderType
 * @returns {object}
 */
export const onClickOrder = (order: string, orderAs: string, orderType: string) => {
  let orderOption = order;
  let listOrderAs = orderAs;
  if (orderType !== orderOption) {
    listOrderAs = "";
  }
  if (listOrderAs === "") {
    orderOption = orderType;
    listOrderAs = "asc";
  } else {
    if (orderAs === "asc") {
      listOrderAs = "desc";
    } else {
      orderOption = "";
      listOrderAs = "";
    }
  }
  return { orderOption, listOrderAs }
};


/**
 * check survey user modules
 * @param user
 * @returns {boolean}
 */
export const hasSurveyModules = (user: any) => {
  if (
    user &&
    user.modules &&
    (user.modules.includes("FEDERFARMA_ENROLLMENT") ||
      user.modules.includes("FEDERFARMA_SURVEYS"))
  ) {
    return true
  }
  else {
    return false
  }
};

/**
 * returns navigation path
 * @param user - user's modules object
 * @returns {string}
 */
export const getNavigationPath = (user: any) => {

  if (user.modules.includes("FEDERFARMA_ENROLLMENT")) {
    return '/enrollment'
  }

  if (user.modules.includes("FEDERFARMA_SURVEYS")) {
    return '/surveys'
  }
  return '/prenotation'

};

/**
 * Returns a translated error message.
 * @param error - Generic error to be checked.
 * @returns {string}
 */
export const getErrorMessage = (error: any) => {
  let messageError = ''
  if (error instanceof Error) {
    messageError = error?.message;
  } else {
    messageError = error.key
      ? t(`error.${error.key}`, { param: error.param })
      : t('error.SERVER_ERROR')
  }
  return messageError
}

// return translated label
export const getTranslatedLabel = (formField: any) => {
  const lang = i18n.language
  const translation = formField.find((t: any) => t.iso === lang)
  return translation ? translation.value : ''
}

// return valid email regrex pattern
export const emailPattern = () => {
  return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
}

/**
 * returns option array
 * @param {Option} option - The `option` where translations must be filled.
 */
export const getTranslatedOptions = (
  options: any,
) => {
  const lang = i18n.language
  return options.map((option: any) => {
    const label = option.translationsObject[lang]
    return { key: option.key, name: label }
  })
}

/**
 * returns fiscal code regex
 */
export const getFiscalCodePattern = () => {
  // source link: https://regex101.com/r/sC9dsl/1
  const regex = /^([A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST]{1}[0-9LMNPQRSTUV]{2}[A-Z]{1}[0-9LMNPQRSTUV]{3}[A-Z]{1})$/gm;
  return regex;
}

/**
 * returns empty || error message
 * @param fiscalCode - fiscal code to be validation.
 */
export const validateFiscalCode = (fiscalCode: string) => {
  let errMsg = ''
  const regex = getFiscalCodePattern();

  if (fiscalCode.length !== 16) {
    errMsg = t("required.invalid-fiscal-code-length");
  } else if (!regex.test(fiscalCode)) {
    errMsg = t("required.invalid-fiscal-code");
  }
  return errMsg
}

export const buildParamsUrl = (datas: any) => {
  const params = new URLSearchParams()
  for (const key in datas) {
    if (Array.isArray(datas[key])) {
      for (const value of datas[key]) {
        params.append(key, value)
      }
    } else {
      params.append(key, datas[key])
    }
  }
  return params.toString()
}

export const downloadDocument = (documents: any) => {
  var link: any = document.createElement("a");
  link.href = documents;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

}

/**
  * returns formatted date.
  * @param {Date | null} date - date to be formatted.
  * @returns {Date}
  */
export const getFormattedDate = (date: Date | null) => {
  return date
    ? moment(date, "DD-MM-YYYY").format("YYYY-MM-DD")
    : ""
}

/**
  * returns formatted date for UI DD/MM/YYYY.
  * @param {Date | null} date - date to be formatted.
  * @returns {Date}
  */
export const getUIFormattedDate = (date: Date | null) => {
  return date
    ? moment(date).format("DD/MM/YYYY")
    : ""
}

/**
  * returns formatted date for UI DD/MM/YYYY HH:MM.
  * @param {Date | null} date - date Time to be formatted.
  * @returns {Date}
  */
export const getFormattedDateTime = (date: Date | null) => {
  return date
    ? moment(date).format("DD/MM/YYYY HH:mm")
    : ""
}

/**
  * returns Cookie
  * @param {string} nameCookie - name of cookie.
  * @returns {string}
  */
export const getCookie = (nameCookie: string) => {
  let name = nameCookie + "=";
  let ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

/**
  * Check cookies exist 
  * @param {string} nameCookie - name of cookie.
  * @returns {boolean}
  */
export const checkCookie = (nameCookie: string) => {
  let user = getCookie(nameCookie);
  return Boolean(user)
}
